import { View } from "react-native";
import Svg, { Path } from "react-native-svg";

import { LEG_VIEWBOX, SEATED_LEG, mirrored } from "../../overlay/seatedLeg";

/**
 * The coordinates both drawings are made in.
 *
 * Its own constant, not the leg's, though the two are equal and a test says so.
 * The torso's curves were written against `120 × 160` and reading that from
 * `LEG_VIEWBOX` meant retuning the seated leg's drawing would silently rescale
 * the abdomen's silhouette — a coupling with no reason to exist between two
 * shapes that share nothing but a habit.
 */
const TORSO_VIEWBOX = { width: 120, height: 160 } as const;

/** The leg drawn facing the other way, computed once rather than per render. */
const MIRRORED_LEG = mirrored(SEATED_LEG);

/**
 * Where to stand, drawn as the two curves that say it and nothing else.
 *
 * This is the outline she is shown while she is aiming the phone, before any
 * step is illustrated. It was an oval — honest about depicting nobody, useless
 * at saying what to do with it, since an oval in a camera preview reads as a
 * crop marker rather than as a place to put yourself. Two curves say it: they
 * flare at the shoulders, draw in at the waist, flare again at the hips.
 *
 * ### Shoulders to hips, and no head
 *
 * What has to be in frame is the part the coach works on. The pose model reads
 * four landmarks — two shoulders, two hips — and the injection area is derived
 * between them, so a head in frame buys nothing and drawing one would ask her
 * to stand further back than the feature needs. Her face is also the one part
 * of the picture she has most reason not to want on screen.
 *
 * ### Two open curves, not a body
 *
 * Nothing closes at the top or the bottom: there is no shoulder line, no
 * waistband, no figure. A closed contour is a shape a body either fits or does
 * not, and most bodies would not fit this one. Two curves are a suggestion of
 * where the edges of a torso go, which is all the framing needs — and standing
 * outside them is not an error. Nothing downstream reads this drawing:
 * `framingFrom` decides whether she is framed from her own landmarks and would
 * say the same thing if it were not here.
 *
 * ### What it may not contain
 *
 * No side, no arms, no injection. The preview is mirrored, so a left drawn here
 * is her right — which is why every position the coach derives is a midpoint,
 * and why these two curves are the same curve reflected. Arms would be worse
 * than absent: hers are about to be busy holding a pen against her abdomen. The
 * abdomen stays blank; the injection area is only ever outlined on her real
 * body, once she is framed. See `InjectionZones`.
 *
 * ### The geometry
 *
 * A `120 × 160` viewBox, and `meet`, so the proportions survive whatever shape
 * the box turns out to be. The right curve is the left one reflected through
 * `x = 60`, point for point, so the drawing cannot acquire a side by drift.
 *
 * The box is `aspectRatio: 3/4` **capped at 340 px tall**, so it is 3:4 only at
 * 255 px wide and wider than that everywhere else — this viewBox is not the box,
 * and a comment here used to say it was. Being fitted to the height rather than
 * stretched to the box is the right answer anyway, and not by luck: the preview
 * fills the box with `cover`, so as long as the box is narrower than the camera
 * frame — every phone, and the sheet on a laptop — it is the height that sets
 * how large she appears, and a height-fitted guide keeps describing the same
 * body at the same distance. The case it does not cover is a box *wider* than
 * the camera frame, where `cover` scales by width and she comes out larger than
 * the guide. Worth knowing before this is opened on something very wide.
 *
 * The widths are measured against the height rather than chosen to look right
 * on their own. An average build with her arms at her sides is about as wide
 * across the shoulders as her torso is tall from shoulder to hip, so the two
 * scale together: shoulders 98, waist 80, hips 92, against a torso of 100. The
 * first pass was drawn at 64 across the shoulders and a normal body overflowed
 * it on every side, which turns a target into a verdict.
 *
 * ### How close this asks her to stand
 *
 * The torso runs from `y = 42` to `y = 142` — 62% of the preview's height,
 * against 54% before. That is close to the most this box can ask for: a 3:4
 * portrait frame is narrow relative to a torso, so filling much more of the
 * height would put her shoulders outside the frame entirely. The curves leave
 * the bottom edge rather than stopping short, because her body does continue
 * past it and a guide that closes invites her to fit inside a box she has no
 * reason to fit inside.
 */
const LEFT_SIDE = [
  "M 40 10", // where a shoulder begins, not where a neck ends
  "Q 20 15 11 42", // out to the shoulder
  "Q 16 72 20 98", // in to the waist
  "Q 12 124 14 142", // out to the hip
  "Q 15 153 17 160", // and off the bottom edge, rather than closing
].join(" ");

const RIGHT_SIDE = [
  "M 80 10",
  "Q 100 15 109 42",
  "Q 104 72 100 98",
  "Q 108 124 106 142",
  "Q 105 153 103 160",
].join(" ");

export function FramingSilhouette({
  tone,
  shape = "torso",
  facing = "right",
}: {
  tone: string;
  /**
   * Which part she is being asked to frame.
   *
   * `torso` is the abdomen's: shoulders to hips, standing or seated. `thigh` is
   * the seated leg, and it exists because the torso outline asked her to fit a
   * body into a frame that needs a limb.
   */
  shape?: "torso" | "thigh";
  /**
   * Which way the knee points, on a thigh.
   *
   * The leg is drawn once, knee to the right, and reflected through the middle
   * of the viewBox for the other side rather than drawn twice — the same rule
   * the torso's two curves follow, and for the same reason: a silhouette that
   * exists in two hand-made copies acquires a difference between them.
   *
   * Ignored by the torso, which has no side. See `kneePointsTo`.
   */
  facing?: "left" | "right";
}) {
  return (
    <View
      pointerEvents="none"
      style={{ position: "absolute", left: 0, right: 0, top: 0, bottom: 0 }}
    >
      <Svg
        width="100%"
        height="100%"
        viewBox={
          shape === "thigh"
            ? `0 0 ${LEG_VIEWBOX.width} ${LEG_VIEWBOX.height}`
            : `0 0 ${TORSO_VIEWBOX.width} ${TORSO_VIEWBOX.height}`
        }
        preserveAspectRatio="xMidYMid meet"
      >
        {shape === "thigh" ? (
          <>
            <Path
              d={facing === "left" ? MIRRORED_LEG : SEATED_LEG}
              fill="none"
              stroke={tone}
              strokeWidth="1.6"
              strokeDasharray="4 3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </>
        ) : (
          [LEFT_SIDE, RIGHT_SIDE].map((side) => (
            <Path
              key={side}
              d={side}
              fill="none"
              stroke={tone}
              strokeWidth="1.6"
              strokeDasharray="4 3"
              strokeLinecap="round"
            />
          ))
        )}
      </Svg>
    </View>
  );
}
